'use client';

import { useEffect, useRef, useState } from 'react';

interface SloganSvgProps {
  className?: string;
  style?: React.CSSProperties;
}

export const SloganSvg = ({ className, style }: SloganSvgProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 },
    );

    const currentRef = svgRef.current;

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <svg
      ref={svgRef}
      width="311"
      height="84"
      viewBox="0 0 311 84"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      role="img"
      aria-label="웰로 로컬페스타 2025 슬로건"
    >
      <defs>
        <style>
          {`
            .draw-path {
              stroke-dasharray: 1000;
              stroke-dashoffset: 1000;
              fill: transparent;
              stroke: rgba(255, 255, 255, 0.3);
              stroke-width: 0.5;
            }

            .draw-path.animate {
              animation: draw 2.2s ease-in-out forwards;
            }

            .draw-rect {
              opacity: 0;
            }

            .draw-rect.animate {
              animation: expandWidth 0.7s ease-in-out 1s forwards, fadeIn 0.3s ease-in 1s forwards;
            }

            @keyframes draw {
              to {
                stroke-dashoffset: 0;
                fill: white;
                stroke: rgba(255, 255, 255, 0);
              }
            }

            @keyframes expandWidth {
              from {
                width: 0;
              }
              to {
                width: 52;
              }
            }

            @keyframes fadeIn {
              to {
                opacity: 1;
              }
            }

            /* prefers-reduced-motion 지원 - 애니메이션 비활성화 */
            @media (prefers-reduced-motion: reduce) {
              .draw-path,
              .draw-rect {
                animation: none !important;
                stroke-dashoffset: 0;
                fill: white;
                stroke: rgba(255, 255, 255, 0);
                width: 52;
                opacity: 1;
              }
            }
          `}
        </style>
      </defs>

      <path
        className={`draw-path ${isVisible ? 'animate' : ''}`}
        d="M24.59 8.66V11.34C24.59 14.94 24.59 19.03 23.44 25.22L18.875 24.75C19.97 19.5 20.05 15.52 20.09 12.22H3.47V8.66H24.59ZM26.94 28.03V31.66H0.78V28.03H9.875V18.28H14.34V28.03H26.94ZM54.84 10.25V13.875H51.28V16.47H54.84V20.09H51.28V24.44H46.78V6.31H51.28V10.25H54.84ZM41.81 24.72C47.75 24.72 51.59 26.66 51.59 29.875C51.59 33.06 47.75 35.03 41.81 35C35.75 35.03 32 33.06 32 29.875C32 26.66 35.75 24.72 41.81 24.72ZM41.81 28.125C38.25 28.125 36.44 28.625 36.47 29.875C36.44 31.06 38.25 31.69 41.81 31.69C45.28 31.69 47.06 31.06 47.06 29.875C47.06 28.625 45.28 28.125 41.81 28.125ZM45.41 8.875V12.375H28.41V8.875H34.66V6.19H39.16V8.875H45.41ZM36.91 13.19C41.09 13.19 44.03 15.31 44.03 18.375C44.03 21.47 41.09 23.53 36.91 23.56C32.69 23.53 29.72 21.47 29.75 18.375C29.72 15.31 32.69 13.19 36.91 13.19ZM36.91 16.47C35.25 16.47 34.125 17.09 34.125 18.375C34.125 19.66 35.25 20.25 36.91 20.22C38.59 20.25 39.75 19.66 39.75 18.375C39.75 17.09 38.59 16.47 36.91 16.47ZM69.16 6.69C75.84 6.72 79.66 8.44 79.69 11.56C79.66 14.75 75.84 16.47 69.16 16.47C62.47 16.47 58.625 14.75 58.625 11.56C58.625 8.44 62.47 6.72 69.16 6.69ZM69.16 9.97C65.125 9.97 63.34 10.5 63.34 11.56C63.34 12.78 65.125 13.16 69.16 13.19C73.25 13.16 75.03 12.78 75.03 11.56C75.03 10.5 73.25 9.97 69.16 9.97ZM82.25 17.625V21.19H56.09V17.625H82.25ZM79.125 22.56V30.16H63.5V31.47H79.81V34.84H59.06V27.03H74.66V25.81H59.06V22.56H79.125ZM51.28 48.31V54.59H55V58.28H51.28V64.34H46.78V48.31H51.28ZM42.28 50.06V53.625H33.97V59.91C38.97 59.89 41.69 59.67 44.69 59.06L45.16 62.59C41.69 63.41 38.41 63.5 31.94 63.5H29.47V50.06H42.28ZM51.28 65.47V76.66H32.34V65.47H51.28ZM36.78 69.03V73.16H46.84V69.03H36.78ZM78.84 48.31V58.75H82.91V62.41H78.84V77.06H74.34V48.31H78.84ZM70.66 50.94V54.56H61.69V66.47C65.55 66.44 68.875 66.2 72.5 65.59L72.97 69.34C68.56 70.09 64.59 70.19 59.66 70.19H57.19V50.94H70.66Z"
      />

      <path
        className={`draw-path ${isVisible ? 'animate' : ''}`}
        style={{ animationDelay: '0.3s' }}
        d="M192.531 70.25V73.91H166.312V70.25H177.156V66.25H169.25V56.375H185.156V53.69H169.219V50.09H189.594V59.875H173.75V62.625H190.25V66.25H181.594V70.25H192.531ZM209.062 49.5C209.062 57.41 205.344 61.84 196 63.94L194.406 60.53C198.016 59.8 200.406 58.75 201.938 57.44L195.219 57.91L194.594 54.59L204.031 54.28C204.141 53.86 204.234 53.41 204.281 52.94H196.062V49.5H209.062ZM218.25 48.31V62.72H213.781V57.84H209.125V54.25H213.781V48.31H218.25ZM218.25 63.72V71.75H203.5V73.19H218.781V76.69H199.094V68.56H213.781V67.16H199.031V63.72H218.25ZM234.688 48.69C241.375 48.72 245.188 50.44 245.219 53.56C245.188 56.75 241.375 58.47 234.688 58.47C228 58.47 224.156 56.75 224.156 53.56C224.156 50.44 228 48.72 234.688 48.69ZM234.688 51.97C230.656 51.97 228.875 52.5 228.875 53.56C228.875 54.78 230.656 55.16 234.688 55.19C238.781 55.16 240.562 54.78 240.562 53.56C240.562 52.5 238.781 51.97 234.688 51.97ZM247.781 59.625V63.19H221.625V59.625H247.781ZM244.656 64.56V72.16H229.031V73.47H245.344V76.84H224.594V69.03H240.188V67.81H224.594V64.56H244.656ZM280.531 48.31V67.34H276.031V48.31H280.531ZM272.969 66.47C272.969 69.375 275.469 72.5 281.938 73.375L280.156 76.91C275.484 76.17 272.312 74.22 270.625 71.59C268.906 74.22 265.734 76.17 261.094 76.91L259.344 73.375C265.781 72.53 268.281 69.34 268.281 66.47V65.97H272.969V66.47ZM264.75 50.03C269.25 50.06 272.688 52.97 272.688 57.09C272.688 61.25 269.25 64.22 264.75 64.22C260.25 64.22 256.781 61.25 256.812 57.09C256.781 52.97 260.25 50.06 264.75 50.03ZM264.75 53.875C262.656 53.875 261.188 55 261.188 57.09C261.188 59.22 262.656 60.41 264.75 60.41C266.781 60.41 268.219 59.22 268.219 57.09C268.219 55 266.781 53.875 264.75 53.875ZM306.844 48.31V58.75H310.906V62.41H306.844V77.06H302.344V48.31H306.844ZM298.656 50.94V54.56H289.688V66.47C293.547 66.44 296.875 66.2 300.5 65.59L300.969 69.34C296.562 70.09 292.594 70.19 287.656 70.19H285.188V50.94H298.656Z"
      />

      <rect
        className={`draw-rect ${isVisible ? 'animate' : ''}`}
        x="98"
        y="59"
        width="52"
        height="3"
        fill="white"
      />
    </svg>
  );
};
